import React, { useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose, withHandlers } from 'recompose';

import { Loader, SpacerVertical } from '@klarna/bubble-ui';
import { Column } from 'moet-core';
import { components as clientComponents } from 'mage-client';
import { components as coreComponents } from 'mage-core';

import { CAPS } from '@/constants';
import { getBpcPayload, getContractRegion } from '@/selectors/bpc';
import { isFetchingBpc as isFetchingBpcSelector } from '@/selectors/loadingStates';
import { getDecodedWidgetToken } from '@/utils/widget';

import { OnboardingComponent } from './components/BaseComponents/OnboardingComponent';
import { Payload } from '@mjolnir/mf-fields-ts/src/Payload';
import { TermsAndConditionsPayload } from './types/BpcPayload';

type AppBaseProps = RouteComponentProps;

type UserProps = {
  userId?: string;
  userEmail?: string;
};

type StateProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  region: string;
  // eslint-disable-next-line react/no-unused-prop-types
  isFetchingBpc?: boolean;
  contractKrn?: string;
  payload?: Payload;
};

type CapabilitiesProps = {
  associateUserToContractCap: (args: {
    body: { user_id: string; email: string };
    params: { contractKrn: string };
    region: string;
  }) => Promise<void>;
  fetchOnboardingCap: (args: { params: { contractKrn: string }; region: string }) => Promise<void>;
  initiateUnderwritingCap: (args: {
    params: { contractKrn: string };
    region: string;
  }) => Promise<void>;
  patchOnboardingCap: (args: {
    body: Payload;
    params: { contractKrn: string };
    region: string;
  }) => Promise<void>;
  submitTermsAndConditionsCap: (args: {
    body: TermsAndConditionsPayload;
    params: { contractKrn: string };
    region: string;
  }) => Promise<void>;
};

type HandlerProps = {
  associateUserToContract: () => Promise<void>;
  fetchOnboarding: () => Promise<void>;
  initiateUnderwriting: () => Promise<void>;
  patchOnboarding: (payload: Payload) => Promise<void>;
  submitTermsAndConditions: (payload: TermsAndConditionsPayload) => Promise<void>;
};

type EnhancedAppProps = AppBaseProps & HandlerProps & StateProps;

const _BpcApp: React.FC<EnhancedAppProps> = ({
  contractKrn,
  payload,
  associateUserToContract,
  fetchOnboarding,
  initiateUnderwriting,
  patchOnboarding,
  submitTermsAndConditions,
}: EnhancedAppProps) => {
  // On the initial render we associate the user Id to the contract and fetch the BPC data
  useEffect(() => {
    associateUserToContract();
    if (payload == null) {
      fetchOnboarding();
    }
  }, []);

  if (contractKrn == null || payload == null) {
    return (
      // @ts-expect-error: FIXME
      <Column alignItems="center">
        <SpacerVertical spaceToken="space/1000" />
        <Loader />
      </Column>
    );
  }

  return (
    <OnboardingComponent
      onboardingKrn={contractKrn}
      initialPayload={payload}
      fetchOnboarding={fetchOnboarding}
      patchOnboarding={patchOnboarding}
      initiateUnderwriting={initiateUnderwriting}
      submitTermsAndConditions={submitTermsAndConditions}
    />
  );
};

export const BpcApp = compose<EnhancedAppProps, AppBaseProps>(
  coreComponents.withTokenHelper(
    (tokenHelper): UserProps => ({
      userId: tokenHelper.getUserId(),
      userEmail: tokenHelper.getUsername(),
    }),
  ),
  connect(
    (state: Partial<GlobalState>): Partial<EnhancedAppProps> => ({
      contractKrn: getDecodedWidgetToken()?.contractKrn,
      payload: getBpcPayload(state),
      region: getContractRegion(state),
      isFetchingBpc: isFetchingBpcSelector(state),
    }),
  ),
  // @ts-expect-error: FIXME
  clientComponents.withPromiseBasedCapabilities({
    associateUserToContractCap: CAPS.BPC_ASSOCIATE_USER,
    fetchOnboardingCap: CAPS.BPC_GET,
    initiateUnderwritingCap: CAPS.BPC_INITIATE_UNDERWRITING,
    patchOnboardingCap: CAPS.BPC_PATCH,
    submitTermsAndConditionsCap: CAPS.BPC_SUBMIT_TERMS_AND_CONDITIONS,
  } as CapabilitiesProps),
  withHandlers<EnhancedAppProps & UserProps & CapabilitiesProps, HandlerProps>({
    associateUserToContract: ({
      associateUserToContractCap,
      contractKrn = '',
      region,
      userId,
      userEmail,
    }) => () =>
      userId && userEmail
        ? associateUserToContractCap({
            body: { user_id: userId, email: userEmail },
            params: { contractKrn },
            region,
          })
        : Promise.resolve(),
    fetchOnboarding: ({ contractKrn, isFetchingBpc, fetchOnboardingCap, region }) => () =>
      !isFetchingBpc
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          fetchOnboardingCap({ params: { contractKrn: contractKrn! }, region })
        : Promise.resolve(),
    initiateUnderwriting: ({ initiateUnderwritingCap, region, contractKrn }) => () =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      initiateUnderwritingCap({ params: { contractKrn: contractKrn! }, region }),
    patchOnboarding: ({ patchOnboardingCap, region, contractKrn }) => (payload: Payload) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      patchOnboardingCap({ body: payload, params: { contractKrn: contractKrn! }, region }),
    submitTermsAndConditions: ({ submitTermsAndConditionsCap, contractKrn, region }) => (
      payload: TermsAndConditionsPayload,
    ) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      submitTermsAndConditionsCap({ body: payload, params: { contractKrn: contractKrn! }, region }),
  }),
)(_BpcApp);
